import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import * as MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import mapboxgl, { MapboxEvent, MapboxOptions } from 'mapbox-gl';
import { Observable, Subject } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { CommonService } from 'src/app/core/services/common.service';
import { RouteService } from 'src/app/core/services/route.service';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { LoadMapData } from 'src/app/core/state/_actions/report.actions';
import { RequestCompanyDatatable } from 'src/app/core/state/_models/company.model';
import { Datatable, MapData, MapReportData } from 'src/app/core/state/_models/report.models';
import { getMapData } from 'src/app/core/state/_selectors/report.selector';
import { TabsDataService } from 'src/app/modules/application/settings/services/tabs-data.service';
import { AlertService, ToastTypes } from 'src/app/shared/services/alert.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';
import { environment } from 'src/environments/environment';
import { LDFlagValue } from 'launchdarkly-js-client-sdk';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-map-view',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  host: {
    '[class.map-accessibility-enabled]': 'enableMapAccessibilityValue'
  }
})
export class MapComponent implements OnInit, OnDestroy {
  @Input('data') mapTitle: any | null =null;
  @Input('tableId') _tableId: string | null = null;
  @Input('reportId') _reportId: any | null = null;
  @Input('fromDashboard') fromDashboard: boolean = false;
  @Input('inputFlexData') inputFlexData: any;
  @Input('isEmbeddedReport') isEmbeddedReport: boolean = false;
  @Output('refreshReportDetails') refreshReportDetails =
    new EventEmitter<any>();
  @Input('isInView') isInView:boolean = false;
  @Input('referenceFieldKey') referenceFieldKey:string = '';
  @Input('lookupfield') lookupfield:any;
  isUserUnAuthorized: boolean = false;
  reportsData: any;
  subsink = new SubSink();
  center: MapboxOptions['center'] = [0, 0];
  zoom: [number] = [4];
  marker: string = '';
  popupData: {
    coordinates: [number, number] | null;
    id: number;
    name: string;
    description: string;
    primaryDescription: string;
    secondaryDescription: string;
  } = {
    coordinates: null,
    id: 0,
    name: '',
    description: '',
    primaryDescription: '',
    secondaryDescription: '',
  };
  locations: any;
  loadedZoom: boolean = false;
  filterFieldOptions: Array<string> = []
  hoverData:any = {
    type: 'geojson',
    data: null,
  };
  showPanel: boolean = false;
  featureProperties:any;
  mapCoordinates:any = [];
  isFilterActive:boolean = false;
  isFiltersAllowed:boolean = false;
  isCustomColor:boolean = false;
  isLimitExceed:boolean = false;
  isMapInteractive:boolean = false;
  markerColorField:any
  mapReportData:MapReportData | null = null;
  seriesData:any;
  geoJSONData:any;
  dTcolumns = ['name', 'value'];
  mapTrigger: Subject<boolean> = new Subject<boolean>();
  mapDataSet:Datatable = <Datatable>{};
  mapDatasetArr: any;
  mapColumns:any = [
    {
      Name:'name',
      FieldName:'name',
      FieldType:'VARCHAR(255)'
    },
    {
      Name:'value',
      FieldName:'value',
      FieldType:'INT',
      ShowSummary:true
    },
  ]
  isLayerClicked:boolean = false;
  totalDataLength:number = 0;
  totalData:any;
  displayingData:number = 0;
  mapContainer: any;
  enableMapAccessibility$!: Observable<LDFlagValue>;
  enableMapAccessibilityValue: boolean = false;

  private hoverSubject: Subject<any> = new Subject();

  constructor(
    private store: Store,
    private route: RouteService,
    private modal: NgbModal,
    private tabData: TabsDataService,
    private commonService:CommonService,
    private cdr:ChangeDetectorRef,
    private alert:AlertService,
    private readonly ldService: LaunchDarklyService,
  ) {
    this.subsink.add(
    this.hoverSubject.pipe(debounceTime(200)).subscribe((data) => {
      if (data) {
        this.popup(data);
      } else {
        this.mouseOut();
      }
    }));
  }

  ngOnInit(): void {
    this.enableMapAccessibility$ = this.ldService.getFlagValue(
      FEATURE_FLAGS.ENABLE_MAP_ACCESSIBILITY,
      false
    );

    this.subsink.add(
      this.enableMapAccessibility$.subscribe((value: LDFlagValue) => {
        this.enableMapAccessibilityValue = !!value;
      })
    );

    if (!this.isEmbeddedReport) {
      this.store.dispatch(
        new LoadMapData(this.tableId, this.reportId, this.fromDashboard)
      );
    } else {
      this.store.dispatch(
        new LoadMapData(this.tableId, this.reportId, this.fromDashboard, false, [this.getEmbeddedCondition()])
      );
    }
    this.loadSubscribers();
    this.subsink.add(
    this.commonService.printForm.subscribe(()=>{
      this.setMapData(this.reportsData)
    }),
    this.commonService.toggleSideBar.subscribe(()=>{
      this.setMapData(this.reportsData)
    }))
  }


  ngOnChanges(){
  }

  /**
   * @returns report id to update
   */
  get reportId() {
    return this._reportId ? this._reportId : this.route.paramMap.reportId;
  }

  get tableId() {
    return this._tableId ? this._tableId : this.route.getTableKey();
  }

  setCenter = (reportData: MapData) => {
    let center = this.mostFrequent(reportData?.Data);
    this.center = [+center[0], +center[1]];
    if (reportData.Data.length === 0) this.center = [-84, 38];
  };

  loadSubscribers = () => {
    this.loadMapDataSubscriber()
  };

  loadMapDataSubscriber(){
    this.subsink.sink = this.store
    .pipe(
      select(getMapData),
      filter(
        (data) =>
          !!data[this.tableId] && !!data[this.tableId][this.reportId]
      ),
      map((data) => data[this.tableId][this.reportId])
    )
    .subscribe((data) => {
      var errorMessage: any = data;
      if (errorMessage == 'User is unauthorized.')
        this.isUserUnAuthorized = true;
      else {
        this.tabData.setData(this.reportId, data, 'map');
        this.setMapData(data);
        this.checkIfDataBoundExceed(data);
        if(data.MapReport?.InteractiveMap){
          this.setMapCoordinates(data);
          this.createDataSet(data.Data);
        }
        this.displayingData = data.Data.length;
        if(!data.IsFilterData){
          this.totalData = data;
          this.totalDataLength = data.Data.length;
          this.setMapFilterData(data);
        }
      }
    });
  }

  setMapCoordinates(data:any){
    this.mapCoordinates = [];
    data.Data.forEach((d:any) => {
      this.mapCoordinates.push({
        properties:{
          ...d
        },
        coordinates: {
          lat:this.normalizeLatLng(d.Latitude),
          lng:this.normalizedLng(d.Longitude)
        }
      })
    })
  }

  setMapFilterData(data:MapData){
    if((!data.MapReport?.InteractiveMap && data.MapReport?.SeriesField1) || (data.MapReport?.InteractiveMap && data.MarkerColorField)) {
      this.isFiltersAllowed = true;
      if(!this.isLimitExceed && data.MarkerColorField.length) this.isCustomColor = true;
      this.isMapInteractive = data.MapReport.InteractiveMap;
      this.markerColorField = data.MarkerColorField;
    }
    this.mapReportData = data.MapReport || null;
    this.seriesData = data.SeriesData;
    if(data.MapReport?.InteractiveMap){
      this.geoJSONData = data.GeoJsonData;
      this.mapColumns[0].Name = data.MapReport?.NameField?.Name;
      this.mapColumns[0].FieldType = (data.MapReport?.NameField?.FieldType !== 'INT' &&
        data.MapReport?.NameField?.FieldType !== 'DOUBLE' &&
        data.MapReport?.NameField?.IsSummaryField &&
        data.MapReport?.NameField?.FieldType !== 'DECIMAL(20,2)') ? 'VARCHAR(255)' : data.MapReport?.NameField?.FieldType
      this.mapColumns[1].Name = data.MapReport?.BoundaryQueryField?.Name;
      this.mapColumns[1].FieldType = (data.MapReport?.BoundaryQueryField?.FieldType !== 'INT' &&
                                  data.MapReport?.BoundaryQueryField?.FieldType !== 'DOUBLE' &&
                                  data.MapReport?.BoundaryQueryField?.IsSummaryField &&
                                  data.MapReport?.BoundaryQueryField?.FieldType !== 'DECIMAL(20,2)') ? 'VARCHAR(255)' : data.MapReport?.BoundaryQueryField?.FieldType
    } else {
      this.filterFieldOptions = data.SeriesData?.find((series:any) => series.Key === data.MapReport?.SeriesField1.Key)?.Value;
    }
  }

  setMapData(data: any) {
    this.reportsData = data;
    this.setCenter(data);
    const features = data.Data.map((d: any, Id: any) => ({
      type: 'Point',
      id: Id,
      properties: {
        id: Id,
        ...d,
      },
      geometry: {
        coordinates: [this.normalizedLng(d.Longitude), this.normalizeLatLng(d.Latitude)],
      },
    }));
    this.locations = {
      features: features,
      type: 'FeatureCollection',
    };
    this.setMarkerImg();
  }

  popup = (properties: any) => {
    if (properties.Name === '' && properties.Description === '') return;
    this.popupData = {
      coordinates: [this.normalizedLng(+properties.Longitude), this.normalizeLatLng(+properties.Latitude)],
      id: properties.Id,
      name: properties.Name,
      description: properties.Description,
      primaryDescription:
        properties.PrimaryDescription != 'null'
          ? properties.PrimaryDescription
          : '',
      secondaryDescription:
        properties.SecondaryDescription != 'null'
          ? properties.SecondaryDescription
          : '',
    };
    this.updatePopupAnchor(properties.Id);
  };

  mouseOut = () => {
    this.popupData = {
      coordinates: null,
      id:0,
      name: '',
      description: '',
      primaryDescription: '',
      secondaryDescription: '',
    };
  };

  mouseIn(data:any){
    this.hoverSubject.next(data);
  }

  mouseOutFromPoint(){
    this.hoverSubject.next(null);
  }

  editLocation = (event:any,feature: any) => {
    event.stopPropagation(); 
    event.preventDefault();
    if(this.showPanel && feature.properties?.id === this.featureProperties?.id){
      this.showPanel = false;
      return
    }
    if(this.showPanel){
      this.showPanel = false;
    }
    this.featureProperties = JSON.parse(JSON.stringify(feature.properties));
    this.showPanel = true;
    this.cdr.detectChanges();
  };

  applyLayerOnMap(event:any){
    if(this.mapContainer.getLayer('myGeoJSONLayer')) {
      this.mapContainer.removeLayer('myGeoJSONLayer');
    }
    if(this.mapContainer.getLayer('myGeoJSONLayer-line')) {
      this.mapContainer.removeLayer('myGeoJSONLayer-line');
    }
    if(this.mapContainer.getSource('myGeoJSONSource')) {
      this.mapContainer.removeSource('myGeoJSONSource');
    } 
    if(event){
      this.mapContainer.addSource('myGeoJSONSource', {
        type: 'geojson',
        data: JSON.parse(event)
      });
      this.mapContainer.addLayer({
        id: 'myGeoJSONLayer',
        type: 'fill',
        source: 'myGeoJSONSource',
        layout: {},
        paint: {
          'fill-color': ['coalesce', ['get', 'fill'],['get', 'fill-color'], '#007cbf'],
          'fill-opacity': ['coalesce', ['get','fill-opacity'],0.4],
          'fill-outline-color': ['coalesce', ['get', 'strokeColor'],['get', 'stroke'], '#000000'],
        }
      });
      this.mapContainer.addLayer({
        id: 'myGeoJSONLayer-line',
        type: 'line',
        source: 'myGeoJSONSource',
        paint: {
          'line-color': ['coalesce', ['get', 'strokeColor'],['get', 'stroke'], '#000000'], // Outline color
          'line-width': ['coalesce', ['get', 'stroke-width'],['get', 'strokeWidth'], 1], // Outline width
          'line-opacity': ['coalesce', ['get', 'stroke-opacity'],['get', 'strokeOpacity'], 0.7] // Optional: you can adjust the opacity of the line
        }
      });
      this.mapContainer.on('click','myGeoJSONLayer',(e:any) => this.onLayerClick(e));
    }
  }

  mostFrequent(newArr: any) {
    let a = [];
    for (let i = 0; i < newArr?.length; i++) {
      a.push({
        lat: Number(Number(newArr[i].Longitude).toFixed(2)),
        lon: Number(Number(newArr[i].Latitude).toFixed(2)),
      });
    }
    let coordinatesMapping: any = {};
    a.forEach((item) => {
      if (!coordinatesMapping[`${item.lat}_${item.lon}`])
        coordinatesMapping[`${item.lat}_${item.lon}`] = 1;
      else ++coordinatesMapping[`${item.lat}_${item.lon}`];
    });
    let max = 0;
    let maxKey = '';
    for (let char in coordinatesMapping) {
      if (coordinatesMapping[char] > max) {
        max = coordinatesMapping[char];
        maxKey = char;
      }
    }
    return maxKey.split('_');
  }

  setMarkerImg = () => {
    this.marker =
      this.reportsData.Marker != null
        ? this.reportsData.Marker
        : '/assets/images/custom-markers/mapbox-marker-purple.png';
  };

  onRender(evt: MapboxEvent) {
    evt.target.resize();
    if (this.loadedZoom) return;
    this.loadedZoom = true;

    const geocoder = new MapboxGeocoder({
      accessToken: environment.mapBoxToken,
      mapboxgl: evt.target as any,
      placeholder: 'Search for an address',
      marker:false
    });
    evt.target.addControl(geocoder,'top-right');
    evt.target.addControl(new mapboxgl.NavigationControl({
      showCompass: false,
      showZoom: true
    }), "bottom-right");
    evt.target.addControl(new mapboxgl.FullscreenControl(), "bottom-right");
    this.mapContainer = evt.target;

    setTimeout(() => {
      if (this.enableMapAccessibilityValue) {
        const mapContainerElement = evt.target.getContainer();
        if (!mapContainerElement) {
          return;
        }

        const zoomInBtn = mapContainerElement.querySelector('.mapboxgl-ctrl-zoom-in');
        const zoomOutBtn = mapContainerElement.querySelector('.mapboxgl-ctrl-zoom-out');
        const fullscreenBtn = mapContainerElement.querySelector('.mapboxgl-ctrl-fullscreen');
        const searchInput = mapContainerElement.querySelector('.mapboxgl-ctrl-geocoder input') as HTMLInputElement;
        const searchButton = mapContainerElement.querySelector('.mapboxgl-ctrl-geocoder button');
        const mapboxAttribution = mapContainerElement.querySelector('.mapboxgl-ctrl-attrib-inner');

        if (zoomInBtn) {
          zoomInBtn.setAttribute('aria-label', 'Zoom in. Press Enter or Space to zoom in on the map');
          zoomInBtn.setAttribute('title', 'Zoom in');
        }
        if (zoomOutBtn) {
          zoomOutBtn.setAttribute('aria-label', 'Zoom out. Press Enter or Space to zoom out on the map');
          zoomOutBtn.setAttribute('title', 'Zoom out');
        }
        if (fullscreenBtn) {
          fullscreenBtn.setAttribute('aria-label', 'Expand to fullscreen. Press Enter or Space to toggle fullscreen mode');
          fullscreenBtn.setAttribute('title', 'Toggle fullscreen');
        }
        if (searchInput) {
          searchInput.setAttribute('aria-label', 'Search for an address');
          searchInput.setAttribute('placeholder', 'Search for an address');
          searchInput.setAttribute('type', 'text');
        }
        if (searchButton) {
          searchButton.setAttribute('aria-label', 'Submit search');
          searchButton.setAttribute('type', 'submit');
        }
        if (mapboxAttribution) {
          mapboxAttribution.removeAttribute('role');
        }
      }
    }, 100);
  }

  closeMarkerDetails(){
    this.showPanel = false
  }

  disableFilterPanel(){
    this.isFilterActive = false;
  }

  onLayerClick(event:any){
    this.isLayerClicked = true;
    const features = (event.target as mapboxgl.Map).queryRenderedFeatures(event.point);
    if (features.length > 0) {
      const clickedPolygon:any = features[0];
      const coordinates = clickedPolygon.geometry.coordinates;
      const inboundCord = this.mapCoordinates.filter((point:any)=> this.isPointInPolygon(point.coordinates,coordinates[0]));
      this.createDataSet(inboundCord.map((data:any)=> {
        return data.properties;
      }))
    }
  }

  resetLayerClick(){
    this.isLayerClicked = false;
    this.createDataSet(this.reportsData.Data);
  }

  isPointInPolygon(point: { lat: number, lng: number }, polygon: any[]): boolean {
    let inside = false;
    
    const x =Number(point.lng), y = Number(point.lat);
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0], yi = polygon[i][1];
      const xj = polygon[j][0], yj = polygon[j][1];
  
      const intersect = ((yi > y) !== (yj > y))
        && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }
  private colorMap = new Map<string | number, string>(); // Cache for colors


  getColorForValue(value: any): string {
    if (typeof value === 'string' && this.isValidColor(value)) {
      return value;
    }

    if (value === undefined || value === null || value === '') {
      return 'blue'; // Light gray as default
    }

    if (this.colorMap.has(value)) {
      return this.colorMap.get(value)!;
    }

    // List of predefined colors
    const predefinedColors = [
      '#4B3FFF',
      '#5069FF',
      '#7F8CA0',
      '#F86A0B',
      '#000000',
      '#DCE6F4',
      '#31414E',
    ];

    const valueStr = String(value);
    const hash = this.hashCode(valueStr);
    const hueOffset = Math.abs(hash) % predefinedColors.length;

    // Get hue from predefined colors
    const hue = this.getHueFromColor(predefinedColors[hueOffset]);
    const saturation = 70 + Math.abs(hash) % 30; // Adjust saturation
    const lightness = 50 + Math.abs(hash) % 20; // Adjust lightness
    const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

    this.colorMap.set(value, color);

    return color;
  }

  hashCode(str: string): number {
    let hash = 0;
    if (str.length === 0) {
      return hash;
    }
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash;
  }

  getHueFromColor(color: string): number {
    // Convert hex to RGB
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    color = color.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b;
    });
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);
    if (!result) {
      throw new Error('Invalid color format');
    }
    let r = parseInt(result[1], 16) / 255;
    let g = parseInt(result[2], 16) / 255;
    let b = parseInt(result[3], 16) / 255;

    // Find max and min values to get hue
    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let hue = 0;

    if (max === r) {
      hue = (g - b) / (max - min);
    } else if (max === g) {
      hue = 2 + (b - r) / (max - min);
    } else {
      hue = 4 + (r - g) / (max - min);
    }

    hue *= 60; // Convert to degrees
    if (hue < 0) hue += 360; // Make sure hue is positive

    return hue;
  }

  private isValidColor(str: string): boolean {
    const s = new Option().style;
    s.color = str;
    return s.color !== '';
  }

  getMapTableData(dataArr:any){
    return dataArr.map((data: any) => {
      return {
        name: data.Name,
        value: data.BoundaryQueryField,
      };
    });
  }

  createDataSet(data:any){
    this.mapDatasetArr = this.getMapTableData(data);
    this.mapDataSet = {
      Draw: '1',
      GetAll: true,
      RecordsFiltered: this.mapDatasetArr.length,
      RecordsTotal: this.mapDatasetArr.length,
      Data: this.getPaginatedData(
        this.getMapTableData(data),
        0,
        50
      ),
    };
  }

  getPaginatedData(arr: any, start: any, size: any) {
    if (start + size <= arr?.length) return arr?.slice(start, start + size);
    else if (start > arr?.length) return arr;
    return arr?.slice(start);
  }

  payloadListener = (event: RequestCompanyDatatable) => {
    let order = event.Order[0];
    let refactoringDataArr :any= [];
    refactoringDataArr = this.mapDatasetArr ? [...this.mapDatasetArr] : [];
    // sort columns
    let column: any = this.dTcolumns[order?.column];
    if (order?.dir === 'asc' && order?.column >= 0) {
      refactoringDataArr?.sort((a: any, b: any) =>
        a[column] > b[column] ? 1 : a[column] < b[column] ? -1 : 0
      );
    } else if (order?.dir === 'desc' && order?.column >= 0) {
      refactoringDataArr?.sort((a: any, b: any) =>
        a[column] < b[column] ? 1 : a[column] > b[column] ? -1 : 0
      );
    }

    let searchArr: any = []; // contains all rows which meets search criteria
    let searchCols = event.Columns?.filter((e) => e.search.value !== '');
    let globalSearch = event.Search?.value?.toLowerCase()?.trim();

    searchArr = this.commonService.getFilteredData(searchCols,globalSearch,searchArr,refactoringDataArr,this.dTcolumns);

    let sortArr: any;
    if (searchCols?.length || searchArr.length)
      sortArr = this.getPaginatedData(
        searchArr,
        event.Start,
        event.Length
      );
    else if (globalSearch) {
      sortArr = [];
    } else
      sortArr = this.getPaginatedData(
        refactoringDataArr,
        event.Start,
        event.Length
      );

      this.mapDatasetArr = refactoringDataArr;
      this.mapDataSet = {
        Draw: '1',
        GetAll: true,
        RecordsFiltered: (searchCols?.length || searchArr.length || globalSearch || event?.ConditionGroups?.length) ? searchArr?.length : refactoringDataArr?.length,
        RecordsTotal: (searchCols?.length || searchArr.length || globalSearch || event?.ConditionGroups?.length) ? searchArr?.length : refactoringDataArr?.length,
        Data: sortArr,
      };
  };

  resetMap(){
    if(this.mapContainer.getLayer('myGeoJSONLayer')) {
      this.mapContainer.removeLayer('myGeoJSONLayer');
    }
    if(this.mapContainer.getLayer('myGeoJSONLayer-line')) {
      this.mapContainer.removeLayer('myGeoJSONLayer-line');
    }
    if(this.mapContainer.getSource('myGeoJSONSource')) {
      this.mapContainer.removeSource('myGeoJSONSource');
    } 
    this.displayingData = this.totalDataLength;
    this.isLayerClicked = false;
    this.setMapData(this.totalData);
    this.createDataSet(this.totalData.Data);
    this.isFilterActive = false;
  }

  normalizeLatLng(lat:any) {
    let normalizedLat = lat % 360;
    if (normalizedLat > 90) {
      normalizedLat = 180 - normalizedLat;
    } else if (normalizedLat < -90) {
      normalizedLat = -180 - normalizedLat;
    }

    return normalizedLat;
  }

  normalizedLng(lng:any){
    let normalizedLng = lng % 360;
    if (normalizedLng > 180) {
      normalizedLng -= 360;
    } else if (normalizedLng < -180) {
      normalizedLng += 360;
    }

    return normalizedLng;
  }

  checkIfDataBoundExceed(data:MapData){
    if(data.MapReport?.InteractiveMap && data.MarkerColorField){
      if(data.MarkerLimit && data?.MarkerColorField?.length > data?.MarkerLimit){
        this.isCustomColor = false;
        this.isLimitExceed = true;
      }
    } else if(!data.MapReport?.InteractiveMap && data.MapReport?.SeriesField1){
      if(data.MarkerLimit && data.SeriesData.find((series:any) => series.Key === data.MapReport?.SeriesField1Key)?.Value?.length > data?.MarkerLimit){
        this.isCustomColor = false;
        this.isLimitExceed = true;
      }
    }
    if(this.isLimitExceed){
      this.alert.toast(
        ToastTypes.ERROR,
        'Error',
        `Series Field has more than ${ data.MarkerLimit} distinct values. Please choose another one`
      )
    }
  }

  popupAnchor: any = 'bottom';
  popupOffSet:any = [0,-30];

  updatePopupAnchor(point: any) {
    const map = this.mapContainer; 
    const mapWidth = map._containerWidth;
    const mapHeight = map._containerHeight;
    const buffer = 150;
    const mapTop = map.getCanvasContainer().getBoundingClientRect().y;
    const mapLeft = map.getCanvasContainer().getBoundingClientRect().x;
    let anchor = 'bottom';

    const markerElement = document.getElementById(`marker-${point}`)
    const pixelPointX = markerElement?.getBoundingClientRect().x;
    const pixelPointY = markerElement?.getBoundingClientRect().y;
    if(pixelPointY){
      const markerPoint = pixelPointY - mapTop;
      if (markerPoint > buffer || (markerPoint > (mapHeight - buffer))) {
        anchor = 'bottom';
        this.popupOffSet = [0,-30];
      } else {
        anchor = 'top';
        this.popupOffSet = [0,25];
      }
    }
    

    this.popupAnchor = anchor;
  }

  getEmbeddedCondition(){
    return {
      Type: 'all',
      Conditions: [
        {
          ConditionField: { Key:this.referenceFieldKey, IsRelation:true},
          OperationType:'is equal',
          Value:this.lookupfield
        }
      ]
    }
  }

  ngOnDestroy() {
    this.subsink.unsubscribe();
  }
}
